import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ImageUploader from './ImageUploader';

const PostEditor = ({ post, onSave, onCancel }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [author, setAuthor] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryError, setCategoryError] = useState('');

  useEffect(() => {
    // Fetch categories from API
    const fetchCategories = async () => {
      try {
        const response = await axios.get('/api/categories');
        setCategories(response.data.map(cat => cat.name));
      } catch (error) {
        console.error('Error fetching categories:', error);
        // Fallback to default categories if API fails
        setCategories([
          'AI Innovation', 
          'Machine Learning', 
          'Ethics', 
          'Healthcare', 
          'Business', 
          'Case Studies', 
          'Tutorials', 
          'News'
        ]);
      }
    };

    fetchCategories();

    if (post) {
      setTitle(post.title || '');
      setContent(post.content || '');
      setCategory(post.category || '');
      setTags(post.tags ? post.tags.join(', ') : '');
      setImageUrl(post.imageUrl || '');
      setAuthor(post.author || '');
    } else {
      // Default values for new post
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setAuthor(user.username || 'Admin');
    }
  }, [post]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const postData = {
      title,
      content,
      category,
      tags: tags.split(',').map(tag => tag.trim()).filter(tag => tag),
      imageUrl,
      author
    };

    if (post && post._id) {
      postData._id = post._id;
    }

    try {
      await onSave(postData, !post);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error saving post:', error);
    }
  };

  const togglePreview = () => {
    setPreviewMode(!previewMode);
  };

  const handleImageUploaded = (url) => {
    setImageUrl(url);
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) {
      setCategoryError('Category name cannot be empty');
      return;
    }

    if (categories.includes(newCategory.trim())) {
      setCategoryError('Category already exists');
      return;
    }

    try {
      const response = await axios.post('/api/categories', {
        name: newCategory.trim(),
        description: ''
      });
      
      setCategories([...categories, response.data.name]);
      setCategory(response.data.name);
      setNewCategory('');
      setShowCategoryForm(false);
      setCategoryError('');
    } catch (error) {
      console.error('Error creating category:', error);
      setCategoryError('Failed to create category. Please try again.');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6 flex justify-between items-center">
        <h3 className="text-2xl font-semibold text-secondary-800">
          {post ? 'Edit Post' : 'Create New Post'}
        </h3>
        <button
          onClick={togglePreview}
          className={`px-4 py-2 rounded-md ${
            previewMode 
              ? 'bg-gray-200 hover:bg-gray-300 text-gray-800' 
              : 'bg-primary hover:bg-primary-dark text-white'
          }`}
        >
          {previewMode ? 'Edit' : 'Preview'}
        </button>
      </div>

      {previewMode ? (
        <div className="border rounded-lg p-6">
          <div className="category-badge inline-block mb-2">{category}</div>
          <h1 className="text-3xl font-bold mb-4 text-secondary-800">{title}</h1>
          
          <div className="flex items-center mb-6">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold mr-3">
              {author.charAt(0)}
            </div>
            <div>
              <p className="font-medium text-secondary-800">{author}</p>
              <p className="text-sm text-secondary-500">{new Date().toLocaleDateString('en-US', { 
                month: 'long', 
                day: 'numeric', 
                year: 'numeric' 
              })}</p>
            </div>
          </div>
          
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt={title} 
              className="w-full h-64 object-cover rounded-lg mb-6" 
            />
          )}
          
          <div className="prose max-w-none">
            {content.split('\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
          
          {tags && (
            <div className="tags mt-6">
              {tags.split(',').map((tag, index) => (
                <span key={index} className="tag">#{tag.trim()}</span>
              ))}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="form-input"
              placeholder="Enter post title"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="form-group">
              <label className="form-label">Category</label>
              <div className="flex">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="form-input rounded-r-none flex-grow"
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowCategoryForm(true)}
                  className="bg-primary text-white px-3 py-2 rounded-r-md hover:bg-primary-dark"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Tags (comma separated)</label>
              <input
                type="text"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                className="form-input"
                placeholder="ai, machine learning, tutorial"
              />
            </div>
          </div>

          {showCategoryForm && (
            <div className="bg-gray-50 p-4 rounded-md mb-6">
              <h4 className="text-lg font-semibold mb-2">Add New Category</h4>
              <div className="flex items-center">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  className="form-input rounded-r-none flex-grow"
                  placeholder="Enter category name"
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="bg-primary text-white px-4 py-2 rounded-r-md hover:bg-primary-dark"
                >
                  Add
                </button>
              </div>
              {categoryError && (
                <p className="text-red-500 text-sm mt-1">{categoryError}</p>
              )}
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryForm(false);
                    setNewCategory('');
                    setCategoryError('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Author</label>
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="form-input"
              required
            />
          </div>

          <ImageUploader onImageUploaded={handleImageUploaded} currentImage={imageUrl} />

          <div className="form-group">
            <label className="form-label">Content (Markdown supported)</label>
            <div className="editor-container">
              <div className="editor-toolbar">
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '# ')}
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '## ')}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '### ')}
                  title="Heading 3"
                >
                  H3
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '**Bold Text**')}
                  title="Bold"
                >
                  B
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '*Italic Text*')}
                  title="Italic"
                >
                  I
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '[Link Text](https://example.com)')}
                  title="Link"
                >
                  🔗
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '![Image Alt Text](https://example.com/image.jpg)')}
                  title="Image"
                >
                  🖼️
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '- List item\n- Another item')}
                  title="Bulleted List"
                >
                  •
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '1. First item\n2. Second item')}
                  title="Numbered List"
                >
                  1.
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '> Blockquote text')}
                  title="Blockquote"
                >
                  "
                </button>
                <button
                  type="button"
                  className="editor-button"
                  onClick={() => setContent(content + '```\ncode block\n```')}
                  title="Code Block"
                >
                  { }
                </button>
              </div>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="form-textarea editor-content"
                rows="15"
                required
              />
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Use Markdown syntax for formatting. Click the toolbar buttons to insert common elements.
            </p>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="btn bg-gray-300 hover:bg-gray-400 text-gray-800"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : post ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PostEditor;
